import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/client';
import AddOrderPopup_Seeds from './AddOrderPopup_Seeds';
import DemandDetailsPopup from './DemandDetailsPopup';
import ColumnFilterPopup from './ColumnFilterPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxesStacked, faBug, faSeedling } from '@fortawesome/free-solid-svg-icons';

const SeedsTab = ({ selectedPlanning, onRefresh, filters, currentView, searchQuery = '', columnFilters = {}, setColumnFilters = () => {} }) => {
  const [tableData, setTableData] = useState([]);
  const [inventoryData, setInventoryData] = useState({});
  const [seedPacketInventoryData, setSeedPacketInventoryData] = useState({});
  const [seedPacketMinGrams, setSeedPacketMinGrams] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [months, setMonths] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ordersData, setOrdersData] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isAddOrderOpen, setIsAddOrderOpen] = useState(false);
  const [currentOrderData, setCurrentOrderData] = useState({ seedsID: '', year: 0, month_num: 0, seedsName: '' });
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState({});
  const [relevantSeeds, setRelevantSeeds] = useState([]);
  const [allDemands, setAllDemands] = useState({});
  const [productionReturnData, setProductionReturnData] = useState({});

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const openDetailsPopup = (seedsid, year, month) => {
    setDetailsData({ sku: seedsid, year, month });
    setIsDetailsOpen(true);
  };

  const closeDetailsPopup = () => {
    setIsDetailsOpen(false);
  };

  useEffect(() => {
    const fetchRelevantSeeds = async () => {
      if (!filters?.selectedBrands?.length) {
        setRelevantSeeds([]);
        return;
      }
      try {
        const response = await api.post('/api/seeds_by_brands', {
          brandNames: filters.selectedBrands
        });
        const seeds = response.data.map(seed => seed.seedsID.toString());
        setRelevantSeeds(seeds);
      } catch (error) {
        console.error('Error fetching relevant seeds:', error);
      }
    };
    fetchRelevantSeeds();
  }, [filters?.selectedBrands]);

  useEffect(() => {
    const fetchStatusesAndSuppliers = async () => {
      try {
        const [statusesRes, suppliersRes] = await Promise.all([
          api.get('/api/orders_statuses'),
          api.get('/api/seeds_suppliers')
        ]);
        setStatuses(statusesRes.data);
        setSuppliers(suppliersRes.data);
      } catch (error) {
        console.error('Error fetching statuses and suppliers:', error);
      }
    };
    fetchStatusesAndSuppliers();
  }, []);

  const fetchOrdersData = async () => {
    const seedsIDs = tableData.map(row => row.seedsid);
    if (seedsIDs.length === 0) return;
    try {
      const response = await api.post('/api/get_seeds_orders_for_tuples', { seedsIDs });
      const ordersMap = response.data.reduce((acc, order) => {
        const id = order.seedsID.toString();
        if (!acc[id]) acc[id] = [];
        acc[id].push(order);
        return acc;
      }, {});
      setOrdersData(ordersMap);
    } catch (error) {
      console.error('Error fetching seeds orders:', error);
    }
  };

  useEffect(() => {
    if (tableData.length > 0) {
      fetchOrdersData();
    }
  }, [tableData]);

  const openAddOrderPopup = (seedsID, year, month, seedsName, order = null) => {
    setCurrentOrderData({ 
      seedsID, 
      year: parseInt(year), 
      month_num: parseInt(month), 
      seedsName,
      initialData: order
    });
    setIsAddOrderOpen(true);
  };

  const closeAddOrderPopup = () => {
    setIsAddOrderOpen(false);
  };

  const handleOrderSave = async () => {
    closeAddOrderPopup();
    await fetchData();
    await fetchOrdersData();
  };

  const fetchData = async () => {
    if (!selectedPlanning) return;
    setIsLoading(true);
    try {
      const seedsIDs = tableData.map(row => row.seedsid);
      const requests = [
        api.get('/api/seeds_forecast', { params: { forecast_id: selectedPlanning } }),
        api.get('/api/seedsinventory_stock'),
        api.get('/api/individualpacketsinventory_monthlyplanseeds', {
          params: { seedsID: seedsIDs.length > 0 ? seedsIDs.join(',') : undefined }
        }),
        api.get('/api/seeds_expected_production_return')
      ];

      const [demandResponse, stockResponse, seedPacketResponse, productionReturnResponse] = await Promise.all(requests);

      const inventoryMap = stockResponse.data.reduce((acc, item) => {
        acc[item.seedsID.toString()] = {
          Available: item.Available || 0,
          InTreatment: item.InTreatment || 0,
          UnderGerminationTest: item.UnderGerminationTest || 0,
          total: item.total || 0,
          // undefined остаётся undefined: поле приходит только с правом
          // finance, и без него $-оценка стока не рисуется вовсе
          cost_per_pound: item.cost_per_pound
        };
        return acc;
      }, {});
      setInventoryData(inventoryMap);

      const seedPacketData = seedPacketResponse?.data?.data || [];
      const seedPacketInventoryMap = Array.isArray(seedPacketData)
        ? seedPacketData.reduce((acc, item) => {
            acc[item.seedsID.toString()] = Number(item.currentQTY) || 0;
            return acc;
          }, {})
        : {};
      const seedPacketMinGramsMap = Array.isArray(seedPacketData)
        ? seedPacketData.reduce((acc, item) => {
            const seedsID = item.seedsID.toString();
            acc[seedsID] = Number(item.min_gperpacket) || 0;
            return acc;
          }, {})
        : {};
      setSeedPacketInventoryData(seedPacketInventoryMap);
      setSeedPacketMinGrams(seedPacketMinGramsMap);
      setProductionReturnData(productionReturnResponse?.data || {});

      const dataMap = new Map();
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      const sortedMonths = [];
      for (let i = 0; i < 12; i++) {
        const year = currentYear + Math.floor((currentMonth + i - 1) / 12);
        const month = ((currentMonth + i - 1) % 12) + 1;
        sortedMonths.push(`${year}-${String(month).padStart(2, '0')}`);
      }

      demandResponse.data.forEach(item => {
        if (item.year && item.month) {
          const monthKey = `${item.year}-${String(item.month).padStart(2, '0')}`;
          if (sortedMonths.includes(monthKey)) {
            const key = `${item.seedsid}-${item.combinedSeedsName}`;
            if (!dataMap.has(key)) {
              dataMap.set(key, {
                seedsid: item.seedsid.toString(),
                seedsName: item.combinedSeedsName,
                months: {}
              });
            }
            dataMap.get(key).months[monthKey] = {
              to_produce: Number(item.total_to_produce_quantity_lb) || 0,
              plan: Number(item.total_plan_quantity_lb) || 0,
              fact: Number(item.total_fact_quantity_lb) || 0
            };
          }
        }
      });

      setMonths(sortedMonths);

      const filteredData = Array.from(dataMap.values());
      setTableData(filteredData);

      // Pre-calculate demands for all rows and months
      const demands = {};
      filteredData.forEach(row => {
        ['to_produce'].forEach(type => {
          const key = `${row.seedsid}-${type}`;
          const rawProdData = productionReturnResponse.data[row.seedsid] || {};
          demands[key] = calculateAllDemands(
            row.months, row.seedsid, type, sortedMonths,
            (seedPacketInventoryMap[row.seedsid] || 0) + (Number(rawProdData.expectedPackets) || 0),
            seedPacketMinGramsMap[row.seedsid] || 0
          );
        });
      });
      setAllDemands(demands);
    } catch (error) {
      console.error('Error fetching seeds data:', error);
      setSeedPacketInventoryData({});
      setSeedPacketMinGrams({});
      setAllDemands({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (onRefresh) onRefresh(fetchData);
    fetchData();
  }, [selectedPlanning, onRefresh, relevantSeeds]);

  const handleDoubleClick = (rowIndex, monthKey, type) => {
    setEditingCell({ rowIndex, monthKey, type });
  };

  const handleSave = async (seedsid, year, month, type, value) => {
    try {
      await api.put('/api/seeds_forecast', {
        forecast_id: selectedPlanning,
        seedsid,
        year: parseInt(year),
        month: parseInt(month),
        [type]: parseFloat(value) || 0.0
      });
      fetchData();
    } catch (error) {
      console.error('Error updating seeds data:', error);
    }
    setEditingCell(null);
  };

  // Demand is CUMULATIVE — always compare against INITIAL packet stock, no carry-over.
  // For each month: how many packets does this month's cumulative demand require?
  // Whatever packets can't cover → must come from seeds stock (in lbs).
  const calculateAllDemands = (monthsData, seedsid, type, months, seedPacketStock, minGrams) => {
    const demands = {};
    const initialPackets = Number(seedPacketStock) || 0;
    const minPounds = minGrams ? minGrams / 453.592 : 0;

    months.forEach(monthKey => {
      const demandInPoundsOriginal = Number(monthsData[monthKey]?.[type] || 0);

      if (minGrams === 0) {
        console.warn(`No min_gperpacket for seedsID: ${seedsid}, type: ${type}, month: ${monthKey}`);
        demands[monthKey] = { demandInPounds: demandInPoundsOriginal, demandInPackets: 0 };
        return;
      }

      const requiredPackets = Math.ceil(demandInPoundsOriginal / minPounds);

      let demandInPounds;
      let demandInPackets;

      if (initialPackets >= requiredPackets) {
        // Packets fully cover this month's cumulative demand
        demandInPounds = 0;
        demandInPackets = requiredPackets;
      } else {
        // Packets partially cover — remainder must come from seeds stock
        demandInPackets = initialPackets;
        const shortfallPackets = requiredPackets - initialPackets;
        demandInPounds = Number((shortfallPackets * minPounds).toFixed(2));
      }

      demands[monthKey] = { demandInPounds, demandInPackets };
      console.log(`calculateAllDemands: seedsID=${seedsid}, type=${type}, month=${monthKey}, originalDemand=${demandInPoundsOriginal}, initialPackets=${initialPackets}, requiredPackets=${requiredPackets}, demandInPackets=${demandInPackets}, demandInPounds=${demandInPounds}`);
    });

    return demands;
  };

  const calculateDemand = (demandInPoundsOriginal, seedsid, type, monthKey) => {
    const key = `${seedsid}-${type}`;
    return allDemands[key]?.[monthKey] || { demandInPounds: Number(demandInPoundsOriginal) || 0, demandInPackets: 0 };
  };

  // Demand is CUMULATIVE — always subtract from INITIAL stock.
  // If a month has no plan data, carry forward the previous month's Left (stock doesn't change if nothing is consumed).
  const calculateRollingLeft = (monthsData, seedsid, type) => {
    const monthlyStock = {};
    const prodData = productionReturnData[seedsid] || {};
    const initialSeedsStock = (Number(inventoryData[seedsid]?.Available) || 0) + (Number(prodData.expectedSeedsReturn) || 0);
    const initialPacketsStock = (Number(seedPacketInventoryData[seedsid]) || 0) + (Number(prodData.expectedPackets) || 0);
    let prevMonthStock = null;

    months.forEach((monthKey) => {
      if (!monthsData[monthKey]?.[type] && prevMonthStock !== null) {
        // Month not filled or filled with 0 — carry forward previous month's Left unchanged
        monthlyStock[monthKey] = prevMonthStock;
      } else {
        const { demandInPounds, demandInPackets } = calculateDemand(monthsData[monthKey]?.[type] || 0, seedsid, type, monthKey);

        const packetsLeft = Math.max(0, initialPacketsStock - demandInPackets);
        const seedsLeft = Number((initialSeedsStock - demandInPounds).toFixed(2));

        monthlyStock[monthKey] = { seedsLeft, packetsLeft };
      }

      prevMonthStock = monthlyStock[monthKey];
      console.log(`calculateRollingLeft: seedsID=${seedsid}, month=${monthKey}, type=${type}, seedsLeft=${monthlyStock[monthKey].seedsLeft}, packetsLeft=${monthlyStock[monthKey].packetsLeft}`);
    });

    return monthlyStock;
  };

  // Orders are NOT cumulative (each month has its own orders), but demand IS cumulative.
  // If a month has no plan data and no new orders, carry forward the previous month's Left.
  const calculateRollingLeftWithOrders = (monthsData, seedsid, type, ordersByMonth) => {
    const monthlyStock = {};
    const prodData = productionReturnData[seedsid] || {};
    const initialSeedsStock = (Number(inventoryData[seedsid]?.Available) || 0) + (Number(prodData.expectedSeedsReturn) || 0);
    const initialPacketsStock = (Number(seedPacketInventoryData[seedsid]) || 0) + (Number(prodData.expectedPackets) || 0);
    let cumulativeOrdersLbs = 0;
    let prevMonthStock = null;

    months.forEach((monthKey) => {
      const ordersThisMonth = ordersByMonth[seedsid]?.[monthKey] || 0;
      cumulativeOrdersLbs += ordersThisMonth;

      if (!monthsData[monthKey]?.[type] && ordersThisMonth === 0 && prevMonthStock !== null) {
        // No plan (or plan is 0) and no new orders — carry forward previous month's Left
        monthlyStock[monthKey] = prevMonthStock;
      } else {
        const { demandInPounds, demandInPackets } = calculateDemand(monthsData[monthKey]?.[type] || 0, seedsid, type, monthKey);

        const packetsLeft = Math.max(0, initialPacketsStock - demandInPackets);
        const seedsLeft = Number((initialSeedsStock + cumulativeOrdersLbs - demandInPounds).toFixed(2));

        monthlyStock[monthKey] = { seedsLeft, packetsLeft };
      }

      prevMonthStock = monthlyStock[monthKey];
      console.log(`calculateRollingLeftWithOrders: seedsID=${seedsid}, month=${monthKey}, type=${type}, cumulativeOrdersLbs=${cumulativeOrdersLbs}, seedsLeft=${monthlyStock[monthKey].seedsLeft}, packetsLeft=${monthlyStock[monthKey].packetsLeft}`);
    });

    return monthlyStock;
  };

  const { ordersByEffectiveMonth, ordersByEffectiveMonthList } = useMemo(() => {
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const totals = {};
    const lists = {};

    Object.entries(ordersData).forEach(([seedsID, orders]) => {
      orders.forEach(order => {
        let effectiveMonth;
        if (order.statusID === 'BO' && order.bo_follow_up_date) {
          const d = new Date(order.bo_follow_up_date);
          if (isNaN(d.getTime())) return;
          effectiveMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        } else if (order.est_del_date) {
          const d = new Date(order.est_del_date);
          if (isNaN(d.getTime())) return;
          effectiveMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        } else {
          return;
        }
        // Undelivered orders with a past delivery date roll into the current month
        if (effectiveMonth < currentMonthKey) effectiveMonth = currentMonthKey;

        if (!totals[seedsID]) totals[seedsID] = {};
        if (!totals[seedsID][effectiveMonth]) totals[seedsID][effectiveMonth] = 0;
        totals[seedsID][effectiveMonth] += Number(order.lbqty) || 0;

        if (!lists[seedsID]) lists[seedsID] = {};
        if (!lists[seedsID][effectiveMonth]) lists[seedsID][effectiveMonth] = [];
        lists[seedsID][effectiveMonth].push(order);
      });
    });

    return { ordersByEffectiveMonth: totals, ordersByEffectiveMonthList: lists };
  }, [ordersData]);

  const getLeftColor = (currentLeft, monthlyData, currentMonthKey, type, seedsid) => {
    const minGrams = Number(seedPacketMinGrams[seedsid]) || 0;
    const minPounds = minGrams ? minGrams / 453.592 : 0;
    const totalStockInPounds = Number((currentLeft.seedsLeft + (currentLeft.packetsLeft * minPounds)).toFixed(2));

    if (totalStockInPounds <= 0) return 'bg-red-600 text-white';

    let remainingStock = totalStockInPounds;
    let monthsCovered = 0;

    const currentMonthIndex = months.indexOf(currentMonthKey);
    if (currentMonthIndex === -1) return 'bg-red-600 text-white';

    for (let i = currentMonthIndex + 1; i < months.length; i++) {
      const monthKey = months[i];
      const { demandInPounds } = calculateDemand(monthlyData[monthKey]?.[seedsid]?.[type] || 0, seedsid, type, monthKey);
      if (remainingStock >= demandInPounds) {
        remainingStock -= demandInPounds;
        monthsCovered++;
      } else {
        break;
      }
    }

    if (monthsCovered >= 3) return 'bg-green-600 text-white';
    if (monthsCovered >= 1) return 'bg-yellow-500 text-white';
    return 'bg-orange-500 text-white';
  };

  const calculateTotal = (type, monthKey) => {
    return tableData.reduce((sum, row) => {
      const data = row.months[monthKey] || { to_produce: 0, fact: 0, plan: 0 };
      const { demandInPounds } = calculateDemand(data[type], row.seedsid, type, monthKey);
      return sum + (Number(demandInPounds) || 0);
    }, 0).toFixed(2);
  };

  const compareOpSeeds = (val, op, threshold) => {
    switch (op) {
      case '>':  return val > threshold;
      case '>=': return val >= threshold;
      case '<':  return val < threshold;
      case '<=': return val <= threshold;
      case '=':  return val === threshold;
      default:   return true;
    }
  };

  // Pre-compute rolling values per row once — shared by filter and render
  const rollingDataMap = useMemo(() => {
    const map = {};
    tableData.forEach(row => {
      map[row.seedsid] = {
        toProduceLeft:           calculateRollingLeft(row.months, row.seedsid, 'to_produce'),
        toProduceLeftWithOrders: calculateRollingLeftWithOrders(row.months, row.seedsid, 'to_produce', ordersByEffectiveMonth),
      };
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, ordersByEffectiveMonth, months, inventoryData, seedPacketInventoryData, allDemands, productionReturnData]);

  const filteredTableData = useMemo(() => {
    let data = tableData;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      data = data.filter(row =>
        String(row.seedsid).toLowerCase().includes(q) ||
        row.seedsName?.toLowerCase().includes(q)
      );
    }
    const activeColFilters = Object.entries(columnFilters).filter(([, f]) => f?.value !== '');
    if (activeColFilters.length > 0) {
      data = data.filter(row => {
        const lwo = rollingDataMap[row.seedsid]?.toProduceLeftWithOrders || {};
        return activeColFilters.every(([mk, f]) =>
          compareOpSeeds(Number(lwo[mk]?.seedsLeft) || 0, f.operator, parseFloat(f.value))
        );
      });
    }
    return data;
  }, [tableData, searchQuery, columnFilters, rollingDataMap]);

  if (!selectedPlanning) return <div>Please select a planning first</div>;

  const allMonthsData = tableData.reduce((acc, r) => {
    Object.keys(r.months).forEach(key => {
      acc[key] = acc[key] || {};
      acc[key][r.seedsid] = r.months[key];
    });
    return acc;
  }, {});

  return (
    <div className="table-wrapper">
      {isLoading ? (
        <div>Loading...</div>
      ) : tableData.length === 0 ? (
        <div>No seeds data available</div>
      ) : (
        <table className="table-planning">
          <thead>
            <tr className="bg-gray-100">
              <th rowSpan="2">Seeds ID</th>
              <th rowSpan="2">Seeds Name</th>
              <th rowSpan="2">Stock</th>
              {months.map(monthKey => {
                const [year, month] = monthKey.split('-');
                const monthName = monthNames[parseInt(month) - 1];
                const span = currentView === 'all' ? 4 : 1;
                return (
                  <th key={monthKey} colSpan={span}>
                    {`${monthName} ${year}`}
                  </th>
                );
              })}
            </tr>
            <tr className="bg-gray-50">
              {months.map(monthKey => (
                <React.Fragment key={monthKey}>
                  {currentView === 'all' && (
                    <>
                      <th>Demand</th>
                      <th>Left</th>
                      <th style={{ whiteSpace: 'nowrap' }}>
                        Left + Order
                        <ColumnFilterPopup
                          filter={columnFilters[monthKey]}
                          onChange={f => setColumnFilters(prev => ({ ...prev, [monthKey]: f }))}
                          onClear={() => setColumnFilters(prev => { const n = { ...prev }; delete n[monthKey]; return n; })}
                        />
                      </th>
                      <th>Order</th>
                    </>
                  )}
                  {currentView === 'left' && <th>Left</th>}
                  {currentView === 'left-order' && (
                    <th style={{ whiteSpace: 'nowrap' }}>
                      Left + Order
                      <ColumnFilterPopup
                        filter={columnFilters[monthKey]}
                        onChange={f => setColumnFilters(prev => ({ ...prev, [monthKey]: f }))}
                        onClear={() => setColumnFilters(prev => { const n = { ...prev }; delete n[monthKey]; return n; })}
                      />
                    </th>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredTableData.map((row, rowIndex) => {
              const stockData = inventoryData[row.seedsid] || {
                Available: 0, InTreatment: 0, UnderGerminationTest: 0, total: 0
              };
              const seedPacketStock = seedPacketInventoryData[String(row.seedsid)] || 0;
              const rd = rollingDataMap[row.seedsid] || {};
              const rollingToProduceLeft           = rd.toProduceLeft           || {};
              const rollingToProduceLeftWithOrders = rd.toProduceLeftWithOrders || {};
              return (
                <tr key={`${row.seedsid}-${row.seedsName}`}>
                  <td>{row.seedsid}</td>
                  <td>{row.seedsName}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                      <svg version="1.1" width="16" height="16" viewBox="0 0 394.72 394.72" style={{ marginRight: '4px', fill: '#6B7280', flexShrink: 0 }}>
                        <path d="M355.76,0h-206.4c-8.231,0.175-14.774,6.968-14.64,15.2v30.16H38.96c-8.231,0.175-14.774,6.968-14.64,15.2v318.96
                          c-0.134,8.232,6.409,15.025,14.64,15.2h206.4c8.231-0.175,14.774-6.968,14.64-15.2v-30.16h95.76
                          c8.231-0.175,14.774-6.968,14.64-15.2V15.2C370.534,6.968,363.991,0.175,355.76,0z M55.36,378.72l18.4-24h137.2l18.4,24H55.36z
                          M221.36,342.08c-1.51-1.962-3.844-3.114-6.32-3.12H69.84c-2.476,0.006-4.81,1.158-6.32,3.12l-23.2,29.76V126.4
                          c27.919,56.333,96.219,79.367,152.552,51.448c22.315-11.06,40.388-29.132,51.448-51.448l0.08,245.52L221.36,342.08z M142.4,173.36
                          c-57.04,0-102-49.12-102-112h204C244.4,124.24,199.52,173.36,142.4,173.36z M245.36,45.36C245.36,45.36,245.36,45.36,245.36,45.36
                          H153.6c-2.171-9.501-3.272-19.214-3.28-28.96h204c0.08,59.84-40.64,107.44-93.92,111.84v-68
                          C260.356,51.978,253.622,45.316,245.36,45.36z M260.4,333.36v-24h60.56l18.4,24H260.4z M354.4,326.56l-23.2-29.76
                          c-1.51-1.962-3.844-3.114-6.32-3.12H260.4V144.24c40.147-3.156,75.758-26.978,94-62.88V326.56z"/>
                        <circle cx="100" cy="100" r="20" fill="#6B7280"/>
                        <circle cx="140" cy="80" r="15" fill="#6B7280"/>
                        <circle cx="180" cy="100" r="18" fill="#6B7280"/>
                      </svg>
                      <span className="visually-hidden">Seeds Packets:</span>
                      <span title="Seed packets in stock (actual)">{seedPacketStock.toFixed(0)}</span>
                      {(productionReturnData[row.seedsid]?.expectedPackets || 0) > 0 && (
                        <>
                          <span style={{ color: '#9ca3af' }}>+</span>
                          <span
                            title="Expected packets from production (In production records)"
                            style={{ color: '#095e1c', fontStyle: 'italic', cursor: 'default' }}
                          >
                            {productionReturnData[row.seedsid].expectedPackets}
                          </span>
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                      <FontAwesomeIcon icon={faBoxesStacked} style={{ marginRight: '4px', fontSize: '14px' }} aria-hidden="true" />
                      <span className="visually-hidden">Available:</span>
                      <span title="Available in stock (actual)">{stockData.Available.toFixed(2)}</span>
                      {(productionReturnData[row.seedsid]?.expectedSeedsReturn || 0) > 0 && (
                        <>
                          <span style={{ color: '#9ca3af' }}>+</span>
                          <span
                            title="Expected seeds return from production (In production records)"
                            style={{ color: '#095e1c', fontStyle: 'italic', cursor: 'default' }}
                          >
                            {(productionReturnData[row.seedsid].expectedSeedsReturn).toFixed(2)}
                          </span>
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', color: 'gray', marginBottom: '4px' }}>
                      <FontAwesomeIcon icon={faBug} style={{ marginRight: '8px', color: 'gray', fontSize: '14px' }} aria-hidden="true" />
                      <span className="visually-hidden">In Treatment:</span>
                      <span>{stockData.InTreatment.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', color: 'gray', marginBottom: '4px' }}>
                      <FontAwesomeIcon icon={faSeedling} style={{ marginRight: '8px', color: 'gray', fontSize: '14px' }} aria-hidden="true" />
                      <span className="visually-hidden">Under Germination Test:</span>
                      <span>{stockData.UnderGerminationTest.toFixed(2)}</span>
                    </div>
                    <hr style={{ margin: '4px 0' }} />
                    <div style={{ fontWeight: 'bold' }}>
                      {(() => {
                        const prodData = productionReturnData[row.seedsid] || {};
                        const combinedTotal = (stockData.total || 0) + (Number(prodData.expectedSeedsReturn) || 0);
                        return (
                          <>
                            <span title="Total (including expected return from production)">{combinedTotal.toFixed(2)}</span>
                            {/* Сервер отдаёт cost_per_pound только с правом finance — без него $ не показываем */}
                            {stockData.cost_per_pound != null && (
                              <span style={{ marginLeft: '8px' }}>/ ${(combinedTotal * (stockData.cost_per_pound || 0)).toFixed(0)}</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  {months.map(monthKey => {
                    const [year, month] = monthKey.split('-');
                    const data = row.months[monthKey] || { to_produce: 0, fact: 0, plan: 0 };
                    return (
                      <React.Fragment key={monthKey}>
                        {currentView === 'all' && (
                          <td className="demand-cell">
                            {filters?.selectedCalculations?.includes('Plan to Produce') && (
                              <div 
                                onDoubleClick={() => handleDoubleClick(rowIndex, monthKey, 'to_produce')} 
                                onClick={() => openDetailsPopup(row.seedsid, year, month)}
                                className="cursor-pointer demand-value"
                              >
                                {editingCell?.rowIndex === rowIndex && editingCell?.monthKey === monthKey && editingCell?.type === 'to_produce' ? (
                                  <input type="number" step="0.01" defaultValue={data.to_produce}
                                    onBlur={(e) => handleSave(row.seedsid, year, month, 'total_to_produce_quantity_lb', e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && e.target.blur()} />
                                ) : (
                                  (() => {
                                    const { demandInPounds, demandInPackets } = calculateDemand(data.to_produce, row.seedsid, 'to_produce', monthKey);
                                    return `${Number(demandInPounds).toFixed(2)} | ${demandInPackets}`;
                                  })()
                                )}
                              </div>
                            )}
                          </td>
                        )}
                        {(currentView === 'all' || currentView === 'left') && (
                          <td className="left-cell">
                            {filters?.selectedCalculations?.includes('Plan to Produce') && (
                              <div className={`left-value ${getLeftColor(rollingToProduceLeft[monthKey] || { seedsLeft: 0, packetsLeft: 0 }, allMonthsData, monthKey, 'to_produce', row.seedsid)}`}>
                                {`${Number(rollingToProduceLeft[monthKey]?.seedsLeft || 0).toFixed(2)} | ${Number(rollingToProduceLeft[monthKey]?.packetsLeft || 0).toFixed(0)}`}
                              </div>
                            )}
                          </td>
                        )}
                        {(currentView === 'all' || currentView === 'left-order') && (
                          <td className="left-plus-order-cell">
                            {filters?.selectedCalculations?.includes('Plan to Produce') && (
                              <div className={`left-value ${getLeftColor(rollingToProduceLeftWithOrders[monthKey] || { seedsLeft: 0, packetsLeft: 0 }, allMonthsData, monthKey, 'to_produce', row.seedsid)}`}>
                                {`${Number(rollingToProduceLeftWithOrders[monthKey]?.seedsLeft || 0).toFixed(2)} | ${Number(rollingToProduceLeftWithOrders[monthKey]?.packetsLeft || 0).toFixed(0)}`}
                              </div>
                            )}
                          </td>
                        )}
                        {currentView === 'all' && (
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {(ordersByEffectiveMonthList[row.seedsid]?.[monthKey] || []).map((ord, i) => {
                                const accent = { S: '#16a34a', OP: '#059669', BO: '#ea580c', C: '#6b7280' }[ord.statusID] || '#6b7280';
                                return (
                                  <div key={ord.id ?? i}
                                    className="text-xs cursor-pointer"
                                    onClick={() => openAddOrderPopup(row.seedsid, year, parseInt(month), row.seedsName, ord)}
                                    style={{ borderLeft: `3px solid ${accent}`, background: '#f8fafc', borderRadius: '3px', padding: '2px 5px', lineHeight: '1.4' }}>
                                    <div style={{ fontWeight: 600 }}>{ord.lbqty} lb</div>
                                    <div style={{ color: accent }}>{ord.statusID}</div>
                                  </div>
                                );
                              })}
                              <button className="planning-btn-add-order"
                                onClick={() => openAddOrderPopup(row.seedsid, year, parseInt(month), row.seedsName)}>
                                +
                              </button>
                            </div>
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <button onClick={fetchData}>Refresh Data</button>
      {isAddOrderOpen && (
        <AddOrderPopup_Seeds
          seedsID={currentOrderData.seedsID}
          year={currentOrderData.year}
          month_num={currentOrderData.month_num}
          seedsName={currentOrderData.seedsName}
          statuses={statuses}
          suppliers={suppliers}
          onClose={closeAddOrderPopup}
          onSave={handleOrderSave}
          initialData={currentOrderData.initialData}
        />
      )}
      {isDetailsOpen && (
        <DemandDetailsPopup
          materialType="seeds"
          sku={detailsData.sku}
          year={detailsData.year}
          month={detailsData.month}
          type={detailsData.type}
          forecastId={selectedPlanning}
          onClose={closeDetailsPopup}
        />
      )}
    </div>
  );
};

export default SeedsTab;